import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';

export default function SalesTab({ pharmacyId, pharmacyData, orders }) {
  const [period, setPeriod] = useState('week');

  const myOrders = useMemo(() => orders.filter(o =>
    o.items?.some(i => i.pharmacyId === pharmacyId || i.pharmacy === pharmacyData?.pharmacyName)
  ), [orders, pharmacyId, pharmacyData]);

  const delivered = myOrders.filter(o => o.status === 'delivered');

  const now = new Date();
  const todayStr = now.toDateString();
  const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

  const periodOrders = period === 'today'
    ? delivered.filter(o => new Date(o.createdAt).toDateString() === todayStr)
    : period === 'week'
      ? delivered.filter(o => new Date(o.createdAt) >= weekAgo)
      : delivered.filter(o => new Date(o.createdAt) >= monthAgo);

  const totalRevenue = periodOrders.reduce((s, o) => s + (o.total || 0), 0);
  const myRevenue = periodOrders.reduce((s, o) => {
    const myItems = o.items?.filter(i => i.pharmacyId === pharmacyId || i.pharmacy === pharmacyData?.pharmacyName) || [];
    return s + myItems.reduce((s2, i) => s2 + (i.basePrice || i.price * 0.9) * i.quantity, 0);
  }, 0);

  const medicineCounts = {};
  periodOrders.forEach(o => {
    o.items?.forEach(item => {
      if (item.pharmacyId === pharmacyId || item.pharmacy === pharmacyData?.pharmacyName) {
        medicineCounts[item.name] = (medicineCounts[item.name] || 0) + item.quantity;
      }
    });
  });
  const topMedicines = Object.entries(medicineCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const chartData = period === 'today'
    ? Array.from({ length: 24 }, (_, i) => {
      const hourOrders = periodOrders.filter(o => new Date(o.createdAt).getHours() === i);
      return {
        label: `${i}:00`,
        orders: hourOrders.length,
        revenue: parseFloat(hourOrders.reduce((s, o) => s + (o.total || 0), 0).toFixed(2)),
      };
    }).filter(d => d.orders > 0 || d.revenue > 0)
    : period === 'week'
      ? Array.from({ length: 7 }, (_, i) => {
        const d = new Date(now - (6 - i) * 24 * 60 * 60 * 1000);
        const dayOrders = delivered.filter(o => new Date(o.createdAt).toDateString() === d.toDateString());
        return {
          label: d.toLocaleDateString('en', { weekday: 'short' }),
          orders: dayOrders.length,
          revenue: parseFloat(dayOrders.reduce((s, o) => s + (o.total || 0), 0).toFixed(2)),
        };
      })
      : Array.from({ length: 30 }, (_, i) => {
        const d = new Date(now - (29 - i) * 24 * 60 * 60 * 1000);
        const dayOrders = delivered.filter(o => new Date(o.createdAt).toDateString() === d.toDateString());
        return {
          label: d.getDate().toString(),
          orders: dayOrders.length,
          revenue: parseFloat(dayOrders.reduce((s, o) => s + (o.total || 0), 0).toFixed(2)),
        };
      });

  const exportSales = () => {
    if (periodOrders.length === 0) { alert('No data to export'); return; }
    const data = periodOrders.map(o => ({
      'Order ID': o.orderId?.slice(-6).toUpperCase() || o.id.slice(-6).toUpperCase(),
      'Date': new Date(o.createdAt).toLocaleDateString(),
      'Time': new Date(o.createdAt).toLocaleTimeString(),
      'Customer': o.customerName || '',
      'Items': o.items?.map(i => `${i.name} x${i.quantity}`).join(', ') || '',
      'Subtotal (GH₵)': (o.subtotal || 0).toFixed(2),
      'Delivery Fee (GH₵)': (o.deliveryFee || 0).toFixed(2),
      'Service Fee (GH₵)': (o.serviceFee || 0).toFixed(2),
      'Total (GH₵)': (o.total || 0).toFixed(2),
      'Your Earnings (GH₵)': (() => {
        const myItems = o.items?.filter(i => i.pharmacyId === pharmacyId || i.pharmacy === pharmacyData?.pharmacyName) || [];
        return myItems.reduce((s, i) => s + (i.basePrice || i.price * 0.9) * i.quantity, 0).toFixed(2);
      })(),
      'Payment Method': o.paymentMethod || '',
      'Payment Ref': o.paymentReference || '',
      'Rider': o.riderName || '',
      'Address': o.deliveryAddress || '',
      'Ghana Post GPS': o.ghanaPostGPS || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales');
    ws['!cols'] = Object.keys(data[0]).map(() => ({ wch: 16 }));
    XLSX.writeFile(wb, `PharmDev_Sales_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>📈 Sales Report</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
            {pharmacyData?.pharmacyName} · {periodOrders.length} orders
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'flex', backgroundColor: '#F1F3F5', borderRadius: 10, padding: 4 }}>
            {[
              { id: 'today', label: 'Today' },
              { id: 'week', label: '7 Days' },
              { id: 'month', label: '30 Days' },
            ].map(p => (
              <button
                key={p.id}
                style={{
                  padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: period === p.id ? 700 : 400,
                  backgroundColor: period === p.id ? '#FFF' : 'transparent',
                  color: period === p.id ? '#1D9E75' : '#888',
                }}
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            style={{ padding: '9px 16px', backgroundColor: '#1A1A2E', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            onClick={exportSales}
          >
            📥 Export
          </button>
        </div>
      </div>

      {/* STAT CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Orders', value: periodOrders.length, icon: '📦', color: '#1D9E75', sub: period === 'today' ? 'Today' : period === 'week' ? 'This week' : 'This month' },
          { label: 'Revenue', value: `GH₵ ${totalRevenue.toFixed(2)}`, icon: '💰', color: '#3B82F6', sub: 'Total collected' },
          { label: 'Your Earnings', value: `GH₵ ${myRevenue.toFixed(2)}`, icon: '🏦', color: '#1D9E75', sub: 'After 10% fee' },
          { label: 'Avg Order', value: periodOrders.length > 0 ? `GH₵ ${(totalRevenue / periodOrders.length).toFixed(2)}` : 'GH₵ 0', icon: '📊', color: '#F59E0B', sub: 'Per order' },
        ].map((card, i) => (
          <div key={i} style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 18, border: '1px solid #E5E7EB', borderTop: `4px solid ${card.color}` }}>
            <p style={{ fontSize: 22, margin: '0 0 8px' }}>{card.icon}</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: card.color, margin: '0 0 4px' }}>{card.value}</p>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>{card.label}</p>
            <p style={{ fontSize: 12, color: '#888', margin: '4px 0 0' }}>{card.sub}</p>
          </div>
        ))}
      </div>

      {/* REVENUE CHART */}
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #E5E7EB' }}>
        <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>
          📊 Orders & Revenue — {period === 'today' ? 'Today by hour' : period === 'week' ? 'Last 7 days' : 'Last 30 days'}
        </h3>
        {chartData.filter(d => d.orders > 0 || d.revenue > 0).length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <p style={{ fontSize: 32 }}>📊</p>
            <p style={{ color: '#888', fontWeight: 600 }}>No sales data for this period</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => [
                  name === 'revenue' ? `GH₵ ${value}` : value,
                  name === 'revenue' ? 'Revenue' : 'Orders'
                ]}
              />
              <Legend />
              <Bar dataKey="orders" fill="#1D9E75" radius={[4, 4, 0, 0]} name="Orders" />
              <Bar dataKey="revenue" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Revenue (GH₵)" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        {/* TOP MEDICINES */}
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB' }}>
          <h3 style={{ fontWeight: 700, marginBottom: 14, fontSize: 15 }}>💊 Top Medicines</h3>
          {topMedicines.length === 0 ? (
            <p style={{ color: '#888', fontSize: 13 }}>No sales data</p>
          ) : topMedicines.map(([name, qty], i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>
                  {i + 1}. {name}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1D9E75' }}>{qty} sold</span>
              </div>
              <div style={{ backgroundColor: '#F3F4F6', borderRadius: 4, height: 6 }}>
                <div style={{
                  backgroundColor: i === 0 ? '#1D9E75' : i === 1 ? '#3B82F6' : i === 2 ? '#F59E0B' : '#888',
                  height: 6, borderRadius: 4,
                  width: `${(qty / (topMedicines[0]?.[1] || 1)) * 100}%`,
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* RECENT ORDERS */}
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB' }}>
          <h3 style={{ fontWeight: 700, marginBottom: 14, fontSize: 15 }}>📋 Recent Orders</h3>
          {periodOrders.length === 0 ? (
            <p style={{ color: '#888', fontSize: 13 }}>No orders in this period</p>
          ) : periodOrders.slice(0, 6).map((order, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 5 ? '0.5px solid #F3F4F6' : 'none' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>
                  #{order.orderId?.slice(-6).toUpperCase() || order.id.slice(-6).toUpperCase()}
                </p>
                <p style={{ fontSize: 11, color: '#888', margin: '2px 0 0' }}>
                  {new Date(order.createdAt).toLocaleDateString()}
                </p>
              </div>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#1D9E75', margin: 0 }}>
                GH₵ {(order.total || 0).toFixed(2)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* FULL ORDER TABLE */}
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>
            📋 All Orders ({periodOrders.length})
          </h3>
        </div>
        {periodOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <p style={{ fontSize: 32 }}>📭</p>
            <p style={{ color: '#888', fontWeight: 600 }}>No orders in this period</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#F7F8FA' }}>
                  {['Order ID', 'Date', 'Customer', 'Items', 'Total', 'Your Earnings', 'Payment', 'Address'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periodOrders.map((order, i) => {
                  const myItems = order.items?.filter(item => item.pharmacyId === pharmacyId || item.pharmacy === pharmacyData?.pharmacyName) || [];
                  const myEarnings = myItems.reduce((s, i) => s + (i.basePrice || i.price * 0.9) * i.quantity, 0);
                  return (
                    <tr key={order.id} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#F9FAFB' }}>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, color: '#1D9E75' }}>
                        #{order.orderId?.slice(-6).toUpperCase() || order.id.slice(-6).toUpperCase()}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6' }}>
                        {order.customerName || '-'}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {order.items?.map(i => i.name).join(', ')}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', fontWeight: 600 }}>
                        GH₵ {(order.total || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', color: '#1D9E75', fontWeight: 600 }}>
                        GH₵ {myEarnings.toFixed(2)}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', color: '#888' }}>
                        {order.paymentMethod || '-'}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {order.deliveryAddress || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#E8F8F2' }}>
                  <td colSpan={4} style={{ padding: 12, fontWeight: 700, color: '#1D9E75' }}>
                    TOTAL ({periodOrders.length} orders)
                  </td>
                  <td style={{ padding: 12, fontWeight: 700 }}>
                    GH₵ {totalRevenue.toFixed(2)}
                  </td>
                  <td style={{ padding: 12, fontWeight: 700, color: '#1D9E75' }}>
                    GH₵ {myRevenue.toFixed(2)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}